<template>
    <LMap :zoom="zoom" :center="center" :options="{zoomSnap: 0.25}" class="flex-grow-1" @ready="onMapReady" ref="container">
        <LControlLayers position="topright"></LControlLayers>
        <MapyCzBasicLayer/>
        <MapyCzOrthoPhotoLayer :visible="false"/>
        <LMarker v-for="marker in comparedMarkers"
                 :key="marker.key"
                 :icon="createComparedIcon(marker)"
                 :lat-lng="marker.coordinates"
                 :z-index-offset="0">
            <LPopup :options="{maxHeight: maxPopupHeight, minWidth: 260, className: 'predict-map-popup'}">
                <div v-for="(estate, index) in marker.estates" :key="index" class="predict-map-popup-card">
                    <div class="d-flex align-items-center">
                        <span :class="['avatar', 'avatar-sm', 'estate-avatar', 'me-2', 'estate-avatar-' + estate.estateType]">
                            <i :class="['fas', estate.icon]"></i>
                        </span>
                        <div class="flex-fill">
                            <div class="fw-semibold fs-13">{{ estate.typeName }}, {{ estate.area }} m²</div>
                            <div class="text-muted fs-12">{{ estate.locationName }}</div>
                        </div>
                    </div>
                    <div class="d-flex flex-wrap gap-2 mt-2 fs-12">
                        <span class="badge bg-primary-transparent">shoda {{ estate.matchPercent }} %</span>
                        <span class="badge bg-secondary-transparent">{{ formatDistance(estate.distanceMeters) }}</span>
                        <span v-if="estate.priceFromPurchaseContract" class="badge bg-success-transparent">kupní smlouva</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-end mt-2">
                        <div>
                            <div class="fw-semibold">{{ estate.price }}</div>
                            <div class="text-muted fs-12">{{ estate.pricePerMeter }} / m²</div>
                        </div>
                        <a v-if="estate.detailUrl !== null" href="javascript:" class="btn btn-sm btn-primary-light" @click.prevent="openDetail(estate.detailUrl)">Detail</a>
                    </div>
                </div>
            </LPopup>
        </LMarker>
        <LMarker v-if="mainEstate !== null"
                 :icon="mainIcon"
                 :lat-lng="mainEstate.coordinates"
                 :z-index-offset="1000">
            <LPopup :options="{maxHeight: maxPopupHeight, minWidth: 260, className: 'predict-map-popup'}">
                <div class="predict-map-popup-card">
                    <span v-if="mainLabel !== null" class="badge bg-primary mb-2">{{ mainLabel }}</span>
                    <div class="d-flex align-items-center">
                        <span :class="['avatar', 'avatar-sm', 'estate-avatar', 'me-2', 'estate-avatar-' + mainEstate.estateType]">
                            <i :class="['fas', mainEstate.icon]"></i>
                        </span>
                        <div class="flex-fill">
                            <div class="fw-semibold fs-13">{{ mainEstate.typeName }}, {{ mainEstate.area }} m²</div>
                            <div class="text-muted fs-12">{{ mainEstate.locationName }}</div>
                        </div>
                    </div>
                    <div class="mt-2">
                        <div class="fw-semibold">{{ mainEstate.value }}</div>
                        <div class="text-muted fs-12">{{ mainEstate.pricePerMeter }} / m²</div>
                    </div>
                </div>
                <div v-if="estatesAtMainSpot.length > 0" class="predict-map-popup-card bg-light fs-12 text-muted py-2">
                    Na stejném místě {{ estatesAtMainSpot.length === 1 ? 'je srovnávaná nemovitost' : 'jsou srovnávané nemovitosti' }}:
                </div>
                <div v-for="(estate, index) in estatesAtMainSpot" :key="index" class="predict-map-popup-card">
                    <div class="d-flex align-items-center">
                        <span :class="['avatar', 'avatar-sm', 'estate-avatar', 'me-2', 'estate-avatar-' + estate.estateType]">
                            <i :class="['fas', estate.icon]"></i>
                        </span>
                        <div class="flex-fill">
                            <div class="fw-semibold fs-13">{{ estate.typeName }}, {{ estate.area }} m²</div>
                            <div class="text-muted fs-12">{{ estate.locationName }}</div>
                        </div>
                    </div>
                    <div class="d-flex flex-wrap gap-2 mt-2 fs-12">
                        <span class="badge bg-primary-transparent">shoda {{ estate.matchPercent }} %</span>
                        <span v-if="estate.priceFromPurchaseContract" class="badge bg-success-transparent">kupní smlouva</span>
                    </div>
                    <div class="d-flex justify-content-between align-items-end mt-2">
                        <div>
                            <div class="fw-semibold">{{ estate.price }}</div>
                            <div class="text-muted fs-12">{{ estate.pricePerMeter }} / m²</div>
                        </div>
                        <a v-if="estate.detailUrl !== null" href="javascript:" class="btn btn-sm btn-primary-light" @click.prevent="openDetail(estate.detailUrl)">Detail</a>
                    </div>
                </div>
            </LPopup>
        </LMarker>
    </LMap>
</template>
<script lang="ts">
import MapyCzBasicLayer from '@/components/maps/MapyCzBasicLayer.vue';
import MapyCzOrthoPhotoLayer from '@/components/maps/MapyCzOrtophotoLayer.vue';
import L from 'leaflet';
import {DivIcon, LatLngBounds, LatLngTuple} from 'leaflet';
import naja from 'naja';
import {computed, defineComponent, nextTick, onBeforeUnmount, PropType, ref} from 'vue';
import {LControlLayers, LMap, LMarker, LPopup} from '@vue-leaflet/vue-leaflet';

/** The estimated estate as the map shows it — the money and the icon come from the server. */
export interface EstimatedEstateMarker {
    estateType: string;
    icon: string;
    typeName: string;
    area: number;
    locationName: string;
    coordinates: LatLngTuple;
    value: string;
    pricePerMeter: string;
}

export interface ComparedEstateMarker {
    estateType: string;
    icon: string;
    typeName: string;
    area: number;
    locationName: string;
    coordinates: LatLngTuple;
    price: string;
    pricePerMeter: string;
    priceFromPurchaseContract: boolean;
    matchPercent: number;
    distanceMeters: number;
    detailUrl: string | null;
}

/** Estates on the same spot share one marker with a count badge. */
interface GroupedMarker {
    key: string;
    coordinates: LatLngTuple;
    estates: ComparedEstateMarker[];
}

export default defineComponent({
    components: {LPopup, MapyCzBasicLayer, MapyCzOrthoPhotoLayer, LControlLayers, LMap, LMarker},
    props: {
        mainEstate: {
            type: Object as PropType<EstimatedEstateMarker | null>,
            default: null,
        },
        comparedEstates: {
            type: Array as PropType<ComparedEstateMarker[]>,
            default: () => [],
        },
        /** Badge over the main estate's popup; null for an estate that is not being valued. */
        mainLabel: {
            type: String as PropType<string | null>,
            default: 'Oceňovaná nemovitost',
        },
    },
    setup(props) {
        const container = ref<any>(null);
        const leafletMap = ref<L.Map | null>(null);
        const maxPopupHeight = ref<number | null>(null);
        let visibilityObserver: IntersectionObserver | null = null;

        const mainSpotKey = computed(() => (props.mainEstate === null ? null : JSON.stringify(props.mainEstate.coordinates)));

        // comparables standing exactly where the estimated estate stands would hide under
        // its pin — they get listed in the pin's popup instead of drawing their own marker
        const estatesAtMainSpot = computed((): ComparedEstateMarker[] =>
            props.comparedEstates.filter(estate => JSON.stringify(estate.coordinates) === mainSpotKey.value));

        const comparedMarkers = computed((): GroupedMarker[] => {
            const groups: {[key: string]: GroupedMarker} = {};

            props.comparedEstates.forEach(estate => {
                const key = JSON.stringify(estate.coordinates);

                if (key === mainSpotKey.value) {
                    return;
                }

                if (!(key in groups)) {
                    groups[key] = {key, coordinates: estate.coordinates, estates: []};
                }

                groups[key].estates.push(estate);
            });

            return Object.values(groups);
        });

        const bounds = computed((): LatLngBounds => {
            const points = comparedMarkers.value.map(marker => marker.coordinates);

            if (props.mainEstate !== null) {
                points.push(props.mainEstate.coordinates);
            }

            if (points.length === 0) {
                return new LatLngBounds([51.1, 12], [48.5, 19]);
            }

            return new LatLngBounds(points).pad(0.3);
        });

        const center = computed(() => bounds.value.getCenter());
        const zoom = ref(10);

        // a single marker collapses the bounds to a point, so fitBounds would zoom to the layer maximum
        const fitBoundsOptions = computed(() => (comparedMarkers.value.length === 0 ? {maxZoom: 16} : {}));

        function markerHtml(estate: EstimatedEstateMarker | ComparedEstateMarker, className: string, badge: string): string {
            return `<span class="predict-marker ${className} estate-avatar-${estate.estateType}">`
                + `<i class="fas ${estate.icon}"></i>${badge}</span>`;
        }

        function createComparedIcon(marker: GroupedMarker): DivIcon {
            const badge = marker.estates.length > 1
                ? `<span class="predict-marker-count">${marker.estates.length}</span>`
                : '';

            return new DivIcon({
                className: '',
                iconSize: [32, 32],
                iconAnchor: [16, 16],
                popupAnchor: [0, -18],
                html: markerHtml(marker.estates[0], 'predict-marker-compared', badge),
            });
        }

        const mainIcon = computed((): DivIcon | undefined => {
            if (props.mainEstate === null) {
                return undefined;
            }

            const badge = estatesAtMainSpot.value.length > 0
                ? `<span class="predict-marker-count">+${estatesAtMainSpot.value.length}</span>`
                : '';

            return new DivIcon({
                className: '',
                iconSize: [40, 40],
                // the rotated disc has its tip at the bottom-left corner of the square,
                // rotated onto the bottom centre — that tip marks the coordinates
                iconAnchor: [20, 40],
                popupAnchor: [0, -40],
                html: markerHtml(props.mainEstate, 'predict-marker-estimated', badge),
            });
        });

        function formatDistance(meters: number): string {
            if (meters < 1000) {
                return `${meters} m daleko`;
            }

            return `${(meters / 1000).toLocaleString('cs', {maximumFractionDigits: 1})} km daleko`;
        }

        // the popup is rendered by Leaflet outside the snippet lifecycle, so the ajax
        // link listeners of the page never bind to it — the request is made directly
        function openDetail(url: string): void {
            leafletMap.value?.closePopup();
            naja.makeRequest('GET', url, null, {history: false, unique: false});
        }

        function waitForVisibility(): void {
            const element = leafletMap.value!.getContainer();

            visibilityObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    nextTick(() => {
                        if (leafletMap.value) {
                            leafletMap.value.invalidateSize();
                            leafletMap.value.fitBounds(bounds.value, fitBoundsOptions.value);
                        }
                    });

                    visibilityObserver?.disconnect();
                    visibilityObserver = null;
                });
            }, {threshold: 0.1});

            visibilityObserver.observe(element);
        }

        function onMapReady(mapInstance: L.Map): void {
            leafletMap.value = mapInstance;
            maxPopupHeight.value = (container.value as unknown as Element).clientHeight * 0.8;
            waitForVisibility();
        }

        onBeforeUnmount(() => {
            visibilityObserver?.disconnect();
        });

        return {
            container,
            maxPopupHeight,
            comparedMarkers,
            estatesAtMainSpot,
            mainIcon,
            center,
            zoom,
            createComparedIcon,
            formatDistance,
            openDetail,
            onMapReady,
        };
    },
});
</script>
